import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import '../styles/Login.css';

// URL de la web pública informativa. Detecta automáticamente si corre
// en local o en el dominio de producción.
const isLocalHost = ['localhost', '127.0.0.1'].includes(window.location.hostname);
const WEBSITE_URL = process.env.REACT_APP_WEBSITE_URL || (isLocalHost ? 'http://localhost:8099' : 'https://pivasa.app');

// RUC fijo de la empresa — el login ya no pide este dato al usuario.
const BUSINESS_RUC = '20100000001';

function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [info, setInfo] = useState(() => {
    const reason = localStorage.getItem('pivasa_logout_reason');
    if (reason) localStorage.removeItem('pivasa_logout_reason');
    return reason === 'inactivity' ? 'Tu sesión se cerró por inactividad. Vuelve a iniciar sesión.' : '';
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
    setInfo('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const isSuperAdmin = form.username.trim().toLowerCase() === 'superadmin';
      const payload = isSuperAdmin
        ? { username: form.username, password: form.password }
        : { ruc: BUSINESS_RUC, username: form.username, password: form.password };
      const res = await api.post('/api/auth/login', payload);
      if (res.data.success) {
        await login(res.data.token, res.data.user);
        navigate(res.data.user.role === 'superadmin' ? '/empresas' : '/');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Credenciales incorrectas');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="login-bg"
      style={{ backgroundImage: `url('${process.env.PUBLIC_URL}/fondo_login.png')` }}
    >
      {/* Overlay oscuro sobre la imagen */}
      <div className="login-overlay" />

      {/* Volver a la web pública */}
      <a href={WEBSITE_URL} className="login-back-link">
        <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 15l-5-5 5-5M7 10h11" />
        </svg>
        Volver a la Web
      </a>

      {/* Panel principal */}
      <div className="login-panel">

        {/* Barra de acento superior */}
        <div className="login-accent-bar" />

        {/* Cabecera — ícono del sistema + título */}
        <div className="login-header">
          <div className="login-logo-wrap">
            <div className="login-system-icon">
              <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                {/* Hexágono exterior */}
                <path
                  d="M32 4L56 18V46L32 60L8 46V18L32 4Z"
                  fill="url(#hexGrad)"
                  fillOpacity="0.18"
                  stroke="url(#hexStroke)"
                  strokeWidth="1.5"
                />
                {/* Edificio central */}
                <rect x="22" y="28" width="20" height="18" rx="1.5" fill="url(#bldGrad)" fillOpacity="0.9"/>
                <rect x="26" y="32" width="4"  height="4"  rx="0.5" fill="white" fillOpacity="0.75"/>
                <rect x="34" y="32" width="4"  height="4"  rx="0.5" fill="white" fillOpacity="0.75"/>
                <rect x="26" y="39" width="4"  height="7"  rx="0.5" fill="white" fillOpacity="0.9"/>
                <rect x="34" y="39" width="4"  height="7"  rx="0.5" fill="white" fillOpacity="0.75"/>
                {/* Gráfico de barras arriba */}
                <rect x="22" y="22" width="3" height="5"  rx="0.8" fill="#38bdf8" fillOpacity="0.9"/>
                <rect x="27" y="19" width="3" height="8"  rx="0.8" fill="#0ea5e9" fillOpacity="0.9"/>
                <rect x="32" y="21" width="3" height="6"  rx="0.8" fill="#38bdf8" fillOpacity="0.9"/>
                <rect x="37" y="17" width="3" height="10" rx="0.8" fill="#0284c7" fillOpacity="0.9"/>
                {/* Puntos de conexión */}
                <circle cx="32" cy="10" r="2"  fill="#00c6ff" fillOpacity="0.8"/>
                <circle cx="50" cy="21" r="1.5" fill="#0ea5e9" fillOpacity="0.6"/>
                <circle cx="14" cy="21" r="1.5" fill="#0ea5e9" fillOpacity="0.6"/>
                <defs>
                  <linearGradient id="hexGrad" x1="8" y1="4" x2="56" y2="60" gradientUnits="userSpaceOnUse">
                    <stop offset="0%"   stopColor="#00c6ff"/>
                    <stop offset="100%" stopColor="#0284c7"/>
                  </linearGradient>
                  <linearGradient id="hexStroke" x1="8" y1="4" x2="56" y2="60" gradientUnits="userSpaceOnUse">
                    <stop offset="0%"   stopColor="#00c6ff" stopOpacity="0.7"/>
                    <stop offset="100%" stopColor="#6366f1" stopOpacity="0.4"/>
                  </linearGradient>
                  <linearGradient id="bldGrad" x1="22" y1="28" x2="42" y2="46" gradientUnits="userSpaceOnUse">
                    <stop offset="0%"   stopColor="#0ea5e9"/>
                    <stop offset="100%" stopColor="#0284c7"/>
                  </linearGradient>
                </defs>
              </svg>
            </div>
          </div>
          <h1 className="login-title">Sistema de Gestión<br />Empresarial <span className="login-title-dx">DevFox</span></h1>
          <p className="login-subtitle">Acceso seguro al sistema</p>
        </div>

        {/* Separador */}
        <div className="login-divider">
          <span className="login-divider-line" />
          <span className="login-divider-text">INICIAR SESIÓN</span>
          <span className="login-divider-line" />
        </div>

        {/* Formulario */}
        <form className="login-form" onSubmit={handleSubmit} noValidate>
          {info && !error && (
            <div className="login-info">
              <svg viewBox="0 0 20 20" fill="currentColor" className="login-info-icon">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-4a1 1 0 100 2 1 1 0 000-2zm-1 4a1 1 0 112 0v4a1 1 0 11-2 0v-4z" clipRule="evenodd"/>
              </svg>
              {info}
            </div>
          )}
          {error && (
            <div className="login-error">
              <svg viewBox="0 0 20 20" fill="currentColor" className="login-error-icon">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
              </svg>
              {error}
            </div>
          )}

          {/* Usuario */}
          <div className="login-field-wrap">
            <span className="field-icon">
              <svg viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"/>
              </svg>
            </span>
            <input
              type="text"
              name="username"
              value={form.username}
              onChange={handleChange}
              placeholder="Usuario"
              className="login-input"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              required
            />
          </div>

          {/* Contraseña */}
          <div className="login-field-wrap">
            <span className="field-icon">
              <svg viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"/>
              </svg>
            </span>
            <input
              type={showPass ? 'text' : 'password'}
              name="password"
              value={form.password}
              onChange={handleChange}
              placeholder="Contraseña"
              className="login-input"
              autoComplete="new-password"
              required
            />
            <button
              type="button"
              className="toggle-pass"
              onClick={() => setShowPass(v => !v)}
              tabIndex={-1}
              aria-label="Mostrar contraseña"
            >
              {showPass ? (
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/>
                  <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/>
                </svg>
              ) : (
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd"/>
                  <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.064 7 9.542 7 .847 0 1.669-.105 2.454-.303z"/>
                </svg>
              )}
            </button>
          </div>

          <button type="submit" className="btn-login" disabled={loading}>
            {loading ? (
              <span className="btn-loading">
                <span className="spinner" />
                Verificando...
              </span>
            ) : (
              <>
                <svg viewBox="0 0 20 20" fill="currentColor" className="btn-icon">
                  <path fillRule="evenodd" d="M3 3a1 1 0 011 1v12a1 1 0 11-2 0V4a1 1 0 011-1zm7.707 3.293a1 1 0 010 1.414L9.414 9H17a1 1 0 110 2H9.414l1.293 1.293a1 1 0 01-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0z" clipRule="evenodd"/>
                </svg>
                Ingresar al Sistema
              </>
            )}
          </button>
        </form>

        {/* Pie del panel */}
        <div className="login-panel-footer">
          <span className="login-version">v1.0.0</span>
          <span className="login-footer-sep">·</span>
          <span className="login-copy">© 2026 DX Software</span>
        </div>

      </div>
    </div>
  );
}

export default LoginPage;
